import { useEffect, useRef, useState, useCallback } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LS_KEY_THREAD = "bk_chat_thread_id";
const LS_KEY_PHONE = "bk_chat_phone";
const LS_KEY_NAME = "bk_chat_name";

/**
 * ChatWidget — floating "Chat with us" bubble anchored to the bottom-right
 * of the customer's Order Status page. First-open form collects name + phone
 * (per owner request) then swaps into a two-side chat panel that polls for
 * new messages every 5s. Thread id + phone are cached in localStorage so
 * a page refresh keeps the conversation open. If the admin starts a chat
 * from an order, we auto-resume by the customer's phone so their message
 * shows up as a bubble even if the customer never messaged first.
 */
export default function ChatWidget({ orderNumber, customerPhone }) {
  const { user } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState(
    typeof window !== "undefined"
      ? localStorage.getItem(LS_KEY_THREAD) || ""
      : ""
  );
  const [phone, setPhone] = useState(
    typeof window !== "undefined"
      ? localStorage.getItem(LS_KEY_PHONE) || ""
      : ""
  );
  const [name, setName] = useState(
    typeof window !== "undefined"
      ? localStorage.getItem(LS_KEY_NAME) || ""
      : ""
  );
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const bottomRef = useRef(null);
  const resumedRef = useRef(false);

  // Auto-resume a thread by phone (order phone, signed-in phone, or a
  // previously used phone) so admin-initiated messages reach the customer.
  const resumePhone = (
    phone ||
    (customerPhone || "").replace(/\D/g, "") ||
    (user?.phone || "").replace(/\D/g, "")
  );
  useEffect(() => {
    if (threadId || resumedRef.current) return;
    const p = resumePhone;
    if (!p || p.length < 10) return;
    resumedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get("/chat/resume", { params: { phone: p } });
        if (data.thread?.id) {
          setThreadId(data.thread.id);
          setPhone(p);
          localStorage.setItem(LS_KEY_THREAD, data.thread.id);
          localStorage.setItem(LS_KEY_PHONE, p);
          setMessages(data.messages || []);
          setUnread(data.thread.unread_customer_count || 0);
        }
      } catch {
        /* no existing thread — fine */
      }
    })();
  }, [threadId, resumePhone]);

  const poll = useCallback(async () => {
    if (!threadId || !phone) return;
    try {
      const { data } = await api.get(`/chat/threads/${threadId}`, {
        params: { phone },
      });
      setMessages(data.messages || []);
      const cust = data.thread?.unread_customer_count ?? 0;
      if (!open) setUnread(cust);
    } catch {
      /* thread missing (maybe admin deleted) — clear local state */
      localStorage.removeItem(LS_KEY_THREAD);
      setThreadId("");
      setMessages([]);
      setUnread(0);
    }
  }, [threadId, phone, open]);

  useEffect(() => {
    if (!threadId) return;
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [threadId, poll]);

  // Zero the unread counter when the panel is opened.
  useEffect(() => {
    if (open && threadId && phone) {
      api
        .post(`/chat/threads/${threadId}/mark-read`, null, {
          params: { phone },
        })
        .catch(() => {});
      setUnread(0);
      // scroll to newest
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        100
      );
    }
  }, [open, threadId, phone]);

  useEffect(() => {
    if (open) {
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        50
      );
    }
  }, [messages, open]);

  const startOrSend = async () => {
    const text = draft.trim();
    if (!text) return;
    const cleanPhone = phone.replace(/\D/g, "");
    if (!name.trim() || cleanPhone.length < 10) {
      toast.error("Please enter your name and a valid 10-digit phone");
      return;
    }
    setSending(true);
    try {
      if (!threadId) {
        const { data } = await api.post("/chat/threads", {
          name: name.trim(),
          phone: cleanPhone,
          order_number: orderNumber || "",
          message: text,
        });
        setThreadId(data.thread.id);
        localStorage.setItem(LS_KEY_THREAD, data.thread.id);
        localStorage.setItem(LS_KEY_PHONE, cleanPhone);
        localStorage.setItem(LS_KEY_NAME, name.trim());
      } else {
        await api.post(
          `/chat/threads/${threadId}/messages`,
          { text },
          { params: { phone: cleanPhone } }
        );
      }
      setDraft("");
      await poll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const needsInfo = !threadId;
  const lastAdminMsg = [...messages].reverse().find((m) => m.sender === "admin");

  return (
    <>
      {!open && unread > 0 && lastAdminMsg && showPreview && (
        <div
          data-testid="chat-preview-bubble"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-5 z-40 w-[min(78vw,260px)] bg-white border-2 border-brand-red rounded-2xl rounded-br-sm shadow-2xl p-3 cursor-pointer animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setShowPreview(false); }}
            className="absolute -top-2 -right-2 bg-stone-200 text-stone-600 rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-stone-300"
            aria-label="Dismiss"
            data-testid="chat-preview-dismiss"
          >
            <X className="w-3 h-3" />
          </button>
          <div className="text-[9px] uppercase tracking-widest font-black text-brand-red mb-0.5">
            Blossom's Kitchen
          </div>
          <div className="text-sm text-stone-800 line-clamp-3 break-words">
            {lastAdminMsg.text}
          </div>
          <div className="text-[10px] text-brand-red font-bold mt-1">Tap to reply →</div>
        </div>
      )}

      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 bg-brand-red text-white w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
          aria-label="Chat with the truck"
        >
          <MessageCircle className="w-6 h-6" />
          {unread > 0 && (
            <span
              data-testid="chat-unread-badge"
              className="absolute -top-1 -right-1 bg-brand-yellow text-brand-red font-black text-xs w-6 h-6 rounded-full flex items-center justify-center border-2 border-white"
            >
              {unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-40 w-[min(92vw,360px)] h-[min(80vh,520px)] bg-white rounded-2xl shadow-2xl border-2 border-brand-red flex flex-col overflow-hidden"
        >
          <div className="chalkboard-band px-4 py-3 flex items-center justify-between">
            <div className="text-brand-yellow font-display text-lg leading-tight">
              Chat with the truck
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
              className="text-white/80 hover:text-white p-1"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-stone-50">
            {messages.length === 0 && !needsInfo && (
              <div className="text-center text-xs text-stone-400 pt-6">
                Say hi! We'll reply here as soon as we can.
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`chat-msg-${m.sender}`}
                className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-snug shadow-sm ${
                  m.sender === "customer"
                    ? "ml-auto bg-brand-red text-white rounded-br-sm"
                    : "mr-auto bg-white text-stone-800 border border-stone-200 rounded-bl-sm"
                }`}
              >
                {m.sender === "admin" && (
                  <div className="text-[9px] uppercase tracking-widest font-black text-brand-red mb-0.5">
                    Blossom's Kitchen
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {needsInfo && (
            <div className="px-3 pt-3 border-t border-stone-200 bg-white space-y-2">
              <input
                data-testid="chat-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
              />
              <input
                data-testid="chat-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (so we can call if needed)"
                inputMode="tel"
                className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm outline-none focus:border-brand-red"
              />
            </div>
          )}

          <div className="p-2 border-t border-stone-200 bg-white flex gap-2">
            <input
              data-testid="chat-message-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  startOrSend();
                }
              }}
              placeholder="Type a message…"
              className="flex-1 px-3 py-2 rounded-full border border-stone-300 text-sm outline-none focus:border-brand-red"
            />
            <button
              data-testid="chat-send-btn"
              onClick={startOrSend}
              disabled={sending || !draft.trim()}
              className="w-10 h-10 rounded-full bg-brand-red text-white flex items-center justify-center disabled:opacity-40 hover:bg-red-800 transition-colors"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
